import { Grid } from '@mui/material';
import { businessData } from 'atoms';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import StickyActionFooter from 'common/StickyActionFooter';
import StatusBadge from 'common/StatusBadge';
import { FormatTime, GetDayName } from 'common/formatters';
import Form from 'pages/Appointment/Partials/Form';
import { useRecoilState } from 'recoil';
import { toasterNotification } from 'utils/toaster';
import { sendEmail } from 'utils/methods';
import ReactDOMServer from 'react-dom/server';
import NewPatientAccount from 'emails/NewPatient';
import PatientAction from 'emails/PatientActionRequired';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState, useMemo, useRef } from 'react';

import AppointmentBadge from '../AppointmentBadge';
import ServicePopup from 'pages/Business/Service/ServicePopup';

// Helper function to remove commas from price strings before parsing
const cleanPrice = (price) => {
    if (typeof price === 'string') {
        return price.replace(/,/g, '');
    }
    return price;
};

const AppointmentPopup = ({
    open,
    onClose,
    handleSubmit,
    formData,
    setFormData,
    getAppointmentList,
    providers = [],
    setProviders,
    providerFullDetails = [],
}) => {
    const { token } = useAuth();
    const [servicesCount, setServicesCount] = useState(1);
    const [services, setServices] = useState([]);
    const [business, setBusiness] = useRecoilState(businessData);
    const [locations, setLocations] = useState([]);
    const [providerAvailabilityData, setProviderAvailabilityData] = useState(null);
    const [initialFormState, setInitialFormState] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResendingEmail, setIsResendingEmail] = useState(false);
    const [hasEmailError, setHasEmailError] = useState(false);
    const [openServicePopup, setOpenServicePopup] = useState(false);
    const [currentServiceIndex, setCurrentServiceIndex] = useState(null);
    const lastFetchedProviderRef = useRef(null);

    // Helper function to check if appointment is cancelled
    const isAppointmentCancelled = () => {
        return formData?.appointmentStatus?.toLowerCase()?.includes('cancelled');
    };

    // Temporary stopgap: paid cancellation inside 72h is currently blocked server-side
    // for any business still on a legacy Express Stripe Connect account
    // (assertStandardAccount in cancellation.service.js), which is most of them right
    // now — so hide the Cancel button for paid appointments inside 72h rather than
    // surface a "contact support" error. Reschedule is unaffected.
    const isPaidWithin72Hours = () => {
        if (formData?.paymentStatus !== 'paid') return false;
        if (!formData?.dateOfService || !formData?.timeOfService) return false;
        const appointmentDate = new Date(`${formData.dateOfService} ${formData.timeOfService}`);
        if (isNaN(appointmentDate.getTime())) return false;
        const hoursUntil = (appointmentDate.getTime() - Date.now()) / (1000 * 60 * 60);
        return hoursUntil <= 72;
    };

    // Helper function to determine if cancel appointment button should be shown.
    const shouldShowCancelButton = () => {
        // Never show cancel button for cancelled appointments
        if (isAppointmentCancelled()) return false;

        if (isPaidWithin72Hours()) return false;

        const status = String(formData?.appointmentStatus || '').toLowerCase();

        // Show cancel button for pending or booked appointments.
        if (status.includes('pending') || status === 'booked') {
            return true;
        }

        // For any other status, don't show cancel button
        return false;
    };

    // Helper function to check if this appointment was created with a new patient
    const isNewPatientAppointment = () => {
        const isNewPatient = (formData?.form_status === 'edit' || formData?.form_status === 'view') && formData?.new_patient && !formData?.patient;
        
        // Debug logging - remove this after testing
        if (formData?.form_status === 'edit' || formData?.form_status === 'view') {
        }
        
        return isNewPatient;
    };

    // Function to check if patient is registered in patients table and get patient data
    const getPatientByEmail = async (email) => {
        if (!email) return null;
        
        if (!token) {
            console.error('No authentication token found');
            return null;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ email: email.toLowerCase().trim() })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                console.error('Error checking patient registration:', result.error || 'Failed to fetch patient');
                return null;
            }

            // Backend returns { success: true, data: [] } or array directly
            const data = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : null);
            
            return data && data.length > 0 ? data[0] : null;
        } catch (error) {
            console.error('Error checking patient registration:', error);
            return null;
        }
    };

    // Function to resend email to patient (registered or new)
    const handleResendEmail = async () => {
        if (!formData?.new_patient || !formData?.id) {
            toasterNotification('error', '', 'Unable to resend email. Patient data not found.');
            return;
        }
        
        if (!token) {
            console.error('No authentication token found');
            toasterNotification('error', '', 'Authentication required. Please log in again.');
            return;
        }
        
        try {
            setIsResendingEmail(true);
            
            const newPatientData = formData.new_patient;
            const patientEmail = newPatientData.email;
            
            // Check if patient is already registered and get their data
            const registeredPatient = await getPatientByEmail(patientEmail);
            
            if (registeredPatient) {
                // Patient is registered - update appointment with patient_id and send confirmation email
                
                // First, update the appointment to link with the existing patient

                // Call backend API to update appointment with patient_id
                const updateResponse = await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments`,
                    {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({
                            id: formData.id,
                            patient_id: registeredPatient.id,
                        })
                    }
                );

                const updateResult = await updateResponse.json();

                if (!updateResponse.ok || updateResult.error) {
                    const errorMessage = updateResult.error || 'Failed to update appointment with patient_id';
                    console.error('Error updating appointment with patient_id:', errorMessage);
                    return;
                }

                // Update local formData to reflect the change
                setFormData(prev => ({
                    ...prev,
                    patient: registeredPatient.id,
                    patient_name: `${registeredPatient.first_name} ${registeredPatient.last_name}` || registeredPatient.name
                }));

                const visitCost = formData?.servicesData?.reduce((sum, item) => sum + Number(cleanPrice(item.price) || 0), 0) || 0;
                const serviceNames = formData?.servicesData?.map((item) => item.serviceName).join(', ') || '';
                
                // Get provider name
                const providerName = formData?.providerDetail ? 
                    `${formData.providerDetail.first_name} ${formData.providerDetail.last_name}` : 
                    'Provider';
                
                // Get location info - use telemedicine URL if telemedicine appointment
                const isTelemedicine = formData?.location_id === 'telemedicine' || formData?.appointment_type === 'telemedicine';
                let location = 'Office Location';
                
                if (isTelemedicine && formData?.providerDetail?.telemedicine_url) {
                    location = formData.providerDetail.telemedicine_url;
                } else if (formData?.location_id) {
                    // Fetch actual location details if location_id exists
                    try {
                        if (!token) {
                            console.error('No authentication token found');
                        } else {
                            const response = await fetch(
                                `${process.env.REACT_APP_SERVER_URL}/api/providers/get-location`,
                                {
                                    method: 'POST',
                                    headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                    },
                                    body: JSON.stringify({ id: formData.location_id })
                                }
                            );

                            const result = await response.json();

                            if (!response.ok || result.error) {
                                console.error('Error fetching location details:', result.error || 'Failed to fetch location');
                            } else {
                                // Backend returns { success: true, data: [] } or array directly
                                const data = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : null);
                                const locationData = Array.isArray(data) && data.length > 0 ? data[0] : data;
                                
                                if (locationData) {
                                    location = `${locationData.name || ''} ${locationData.address || ''} ${locationData.city || ''} ${locationData.state || ''} ${locationData.suite || ''} ${locationData.zip || ''}`.trim();
                                }
                            }
                        }
                    } catch (error) {
                        console.error('Error fetching location details:', error);
                    }
                }
                
                const emailContent = `
                    <!DOCTYPE html>
                    ${ReactDOMServer.renderToString(
                        <PatientAction
                            patientName={registeredPatient.first_name || registeredPatient.name}
                            businessName={business?.name}
                            buisnessEmail={business?.email}
                            appointmentTime={formData?.timeOfService}
                            appointmentDate={formData?.dateOfService}
                            providerName={providerName}
                            visitCost={visitCost}
                            location={location}
                            serviceNames={serviceNames}
                            appointmentId={formData.id}
                        />,
                    )}
                `;
                
                await sendEmail(
                    token,
                    patientEmail, 
                    'Subject: Action Required: Review and Confirm Your Appointment', 
                    ' ', 
                    emailContent, 
                    'Email resent successfully!',
                    true
                );

                // Refresh the appointment list to reflect the updated patient linkage
                if (getAppointmentList) {
                    getAppointmentList();
                }
                
            } else {
                // Patient is not registered - send new patient account creation email
                const emailContent = `
                    <!DOCTYPE html>
                    ${ReactDOMServer.renderToString(
                        <NewPatientAccount
                            patientName={newPatientData.full_name || `${newPatientData.first_name} ${newPatientData.last_name}`}
                            patientEmail={patientEmail}
                            businessName={business?.name}
                            buisnessEmail={business?.email}
                            dob={newPatientData.dob}
                            gender={newPatientData.gender}
                            phoneNumber={newPatientData.contact_number}
                            appointmentId={formData.id}
                        />,
                    )}
                `;
                
                await sendEmail(
                    token,
                    patientEmail, 
                    'Subject: Action Required: Sign up to verify your appointment.', 
                    ' ', 
                    emailContent, 
                    'Email resent successfully!',
                    true
                );
            }

        } catch (error) {
            console.error('Error resending email:', error);
            toasterNotification('error', '', 'Failed to resend email. Please try again.');
        } finally {
            setIsResendingEmail(false);
        }
    };

    // Function to check if email already exists in the database
    const checkEmailExists = async (email) => {
        if (!email) return false;
        
        if (!token) {
            console.error('No authentication token found');
            return false;
        }

        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/patients`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ email: email.toLowerCase().trim(), limit: 1 })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                console.error('Error checking email:', result.error || 'Failed to check email');
                return false;
            }

            // Backend returns { success: true, data: [] } or array directly
            const data = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : []);
            
            return data && data.length > 0;
        } catch (error) {
            console.error('Error checking email:', error);
            return false;
        }
    };

    // Function to validate if all mandatory fields are filled
    const isFormValid = () => {
        // Check if services data is properly filled with required fields
        // Service is now optional, so we only check description and price if they exist
        const hasValidServices = formData?.servicesData && 
            formData?.servicesData.length > 0 && 
            formData?.servicesData.every(service => 
                // Service is optional, so we don't require it
                // service?.service && 
                service?.description && 
                (service?.price !== undefined && service?.price !== null && service?.price !== '' && !isNaN(parseFloat(cleanPrice(service?.price))))
            );

        return (
            // Check for required fields
            formData?.provider && 
            formData?.dateOfService && 
            formData?.timeOfService && 
            // For new appointments, check service data including description and price
            ((formData?.form_status === 'add' && hasValidServices) || 
             formData?.form_status === 'edit') &&
            // Patient info is required - either existing patient ID or new patient details
            (formData?.patient || 
             (formData?.name && formData?.email))
        );
    };

    useEffect(() => {
        if (open) {
            setInitialFormState(JSON.stringify(formData)); // Store initial form data
        } else {
            lastFetchedProviderRef.current = null;
            setProviderAvailabilityData(null);
        }
    }, [open, formData?.form_status]);

    // Check if form has changes
    const hasFormChanges = useMemo(() => {
        if (!initialFormState) return false;
        return JSON.stringify(formData) !== initialFormState;
    }, [initialFormState, formData]);

    // Check if date/time/location have changed (show Reschedule instead of Save)
    const hasRescheduleRelevantChanges = useMemo(() => {
        if (!initialFormState || formData?.form_status !== 'edit') return false;
        try {
            const initial = JSON.parse(initialFormState);
            const normalize = (v) => (v === undefined || v === null ? '' : String(v).trim());
            const dateChanged = normalize(initial.dateOfService) !== normalize(formData?.dateOfService);
            const timeChanged = normalize(initial.timeOfService) !== normalize(formData?.timeOfService);
            const locationChanged = normalize(initial.location_id) !== normalize(formData?.location_id);
            return dateChanged || timeChanged || locationChanged;
        } catch {
            return false;
        }
    }, [initialFormState, formData]);

    const handleClose = () => {
        const hasUnsavedChanges = JSON.stringify(formData) !== initialFormState;

        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }

        setIsSubmitting(false);
        setServicesCount(1); // Reset services count when closing
        setServices([]); // Reset services array when closing
        onClose();
    };

    // Handle Save & Resend - saves the form then sends email
    const handleSaveAndResend = async (e) => {
        e.preventDefault();
        
        if (!isFormValid()) return;
        
        try {
            setIsSubmitting(true);
            
            // Save the form first
            await handleSubmit(e);
            
            // If save was successful, resend the email
            if (formData?.new_patient || formData?.id) {
                await handleResendEmail();
            }
            
            // Close the form after saving and resending
            onClose();
        } catch (error) {
            console.error('Error in handleSaveAndResend:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        // Trim and convert email fields to lowercase
        const processedValue = name === 'email' ? value.trim().toLowerCase() : value;
        setFormData({ ...formData, [name]: processedValue });
    };

    const handleServiceChange = (e, index) => {
        const { name, value } = e.target;
        // "Add New Service" option opens ServicePopup (same as ProviderPopup)
        if (name === 'service' && String(value) === 'add_new_service') {
            setCurrentServiceIndex(index);
            setOpenServicePopup(true);
            return;
        }
        // Typed/custom service name not in list: store name, no id
        const numValue = Number(value);
        const isNumericId = !isNaN(numValue) && String(value).trim() !== '';
        const selectedService = isNumericId ? services.find((service) => service.id === numValue) : null;
        if (name === 'service' && !isNumericId && value !== null && value !== undefined && value !== '') {
            // User typed a custom name – set serviceName, leave service id empty
            setFormData((prevFormData) => {
                const updatedServices = [...(prevFormData?.servicesData || [])];
                if (!updatedServices[index]) updatedServices[index] = {};
                updatedServices[index] = {
                    ...updatedServices[index],
                    service: null,
                    serviceName: value,
                };
                return { ...prevFormData, servicesData: updatedServices };
            });
            return;
        }

        setFormData((prevFormData) => {
            const updatedServices = [...(prevFormData?.servicesData || [])];

            if (!updatedServices[index]) {
                updatedServices[index] = {};
            }

            // Check if service ID is actually changing (not just being set for the first time)
            const previousServiceId = updatedServices[index]?.service;
            const isServiceChanging = name === 'service' && previousServiceId && previousServiceId !== value;

            // Find the previous service to compare description
            const previousService = previousServiceId ? services.find((service) => service.id === Number(previousServiceId)) : null;
            const currentDescription = updatedServices[index]?.description;
            const previousServiceDescription = previousService?.description || '';

            // Auto-populate price when service is selected or changed
            // Strategy mirrors description: populate if no price exists, or update if it matches
            // the previous service's auto-populated price; preserve manually typed prices.
            const currentPrice = updatedServices[index]?.price;
            const hasExistingPrice = currentPrice !== undefined && currentPrice !== null && currentPrice !== '';
            const previousServicePriceFormatted = previousService?.price !== null && previousService?.price !== undefined
                ? parseFloat(previousService.price).toFixed(2)
                : '';

            let autoPrice = null;
            if (name === 'service' && selectedService && selectedService.price !== null && selectedService.price !== undefined) {
                const newPriceValue = parseFloat(selectedService.price);
                if (!isNaN(newPriceValue)) {
                    if (!hasExistingPrice) {
                        autoPrice = newPriceValue.toFixed(2);
                    } else if (isServiceChanging && currentPrice === previousServicePriceFormatted) {
                        autoPrice = newPriceValue.toFixed(2);
                    }
                }
            }

            // Auto-populate description when service is selected or changed in edit mode
            // Strategy:
            // 1. If no description exists, always populate from service
            // 2. If service is changing and description matches the previous service's description, it was auto-populated - update it
            // 3. Otherwise, preserve manually typed descriptions (they don't match the service's description)
            let autoDescription = null;
            if (name === 'service' && selectedService && selectedService.description !== null && selectedService.description !== undefined && selectedService.description !== '') {
                const hasExistingDescription = currentDescription !== undefined && currentDescription !== null && currentDescription !== '';
                
                if (!hasExistingDescription) {
                    // No description exists - always populate (covers both new services and existing services without descriptions)
                    autoDescription = selectedService.description;
                } else if (isServiceChanging && currentDescription === previousServiceDescription) {
                    // Service is being changed and the description matches the previous service's description
                    // This means it was auto-populated, so update it to the new service's description
                    autoDescription = selectedService.description;
                }
                // If description exists and doesn't match the previous service's description, 
                // assume it was manually typed/customized, so preserve it
            }

            // Store the value as-is (with commas) for display, clean only when processing
            updatedServices[index] = {
                ...updatedServices[index],
                [name]: value,
                ...(name === 'service' && { serviceName: selectedService?.name }),
                ...(autoPrice !== null && { price: autoPrice }),
                ...(autoDescription !== null && { description: autoDescription }),
            };
            return {
                ...prevFormData,
                servicesData: updatedServices,
            };
        });
    };

    const cancelAppointment = async (id) => {
        let confirmCancel = window.confirm('Are you sure you want to cancel this appointment?');

        // If the user cancels the confirmation dialog, do NOT proceed
        // and explicitly return false so callers know no action was taken
        if (!confirmCancel) {
            return false;
        }

        try {
            // Validate appointment ID
            if (!id) {
                console.error('Appointment ID is required');
                return false;
            }

            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                return false;
            }

            // Call the dedicated cancellation endpoint — this actually issues a full,
            // zero-fee Stripe refund to the patient (see cancellation.service.js),
            // unlike a raw status update which does nothing with money.
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/appointments/${id}/cancel`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to cancel appointment';
                console.error('Error canceling appointment:', errorMessage);
                toasterNotification('error', '', errorMessage);
                return false;
            }

            toasterNotification('success', 'Appointment cancelled', 'The patient has been fully refunded.');
            onClose();
            return true;
        } catch (error) {
            console.error('Error canceling appointment:', error);
            return false;
        }
    };

    const handleAddAnotherService = () => {
        setServicesCount((prevCount) => {
            const newCount = prevCount + 1;
            // Initialize servicesData array if needed when adding services
            setFormData((prevFormData) => {
                const currentServices = Array.isArray(prevFormData?.servicesData) ? [...prevFormData.servicesData] : [];
                // Ensure the array has enough slots for all services
                while (currentServices.length < newCount) {
                    currentServices.push({});
                }
                return {
                    ...prevFormData,
                    servicesData: currentServices,
                };
            });
            return newCount;
        });
    };

    const handleRemoveService = (index) => {
        // Ensure servicesCount never drops below 1
        setServicesCount((prevCount) => {
            const nextCount = Math.max(1, prevCount - 1);
            
            // Update servicesData by filtering out the item at the specified index
            setFormData((prevFormData) => {
                const currentServices = Array.isArray(prevFormData?.servicesData) ? prevFormData.servicesData : [];
                // Filter out the service at the specified index
                const updatedServices = currentServices.filter((_, i) => i !== index);
                
                return {
                    ...prevFormData,
                    servicesData: updatedServices,
                };
            });
            
            return nextCount;
        });
    };

    const handleReschedule = () => {
        try {
            const prefer = document.getElementById('reschedule-date') || document.getElementById('reschedule-time') || document.getElementById('reschedule-section');
            if (prefer) {
                prefer.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                setTimeout(() => {
                    const input = prefer.querySelector('input') || document.querySelector('#reschedule-section input');
                    if (input) input.focus({ preventScroll: true });
                }, 200);
            }
        } catch (_) {}
    };



    const getProviderLocations = async (providerId) => {
        if (!providerId) { setLocations([]); return; }

        // Use cached data from provider-full-details when available
        const cached = providerFullDetails.find((item) => (item.provider ?? item).id === Number(providerId));
        if (cached) {
            const providerLocations = Array.isArray(cached.availability) ? cached.availability : [];
            setProviderAvailabilityData(providerLocations);

            const locationIds = new Set(
                providerLocations.map((a) => a.location).filter((l) => l && l !== '0' && l !== '')
            );

            const matchedLocations = (Array.isArray(cached.locations) ? cached.locations : [])
                .filter((l) => locationIds.has(l.id) || locationIds.has(String(l.id)))
                .map((l) => ({
                    id: l.id,
                    location: `${l.name} ${l.address} ${l.city} ${l.state} ${l.suite} ${l.zip}`.trim(),
                }));

            setLocations(matchedLocations);
            return;
        }

        if (!token) { setLocations([]); return; }
        try {
            const availabilityResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}/availability`,
                { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
            );

            if (!availabilityResponse.ok) { setLocations([]); return; }

            const availabilityResult = await availabilityResponse.json();
            const providerLocations = Array.isArray(availabilityResult.data)
                ? availabilityResult.data
                : (Array.isArray(availabilityResult) ? availabilityResult : []);

            setProviderAvailabilityData(providerLocations);

            const locationIds = [...new Set(
                providerLocations.map((item) => item.location).filter((l) => l && l !== '0' && l !== '')
            )];

            if (locationIds.length === 0) { setLocations([]); return; }

            const locationsResponse = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/get-location`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ id: locationIds }),
                }
            );

            if (!locationsResponse.ok) { setLocations([]); return; }

            const locationsResult = await locationsResponse.json();
            const data = Array.isArray(locationsResult.data) ? locationsResult.data : (Array.isArray(locationsResult) ? locationsResult : []);

            setLocations(data.map((l) => ({
                id: l.id,
                location: `${l.name} ${l.address} ${l.city} ${l.state} ${l.suite} ${l.zip}`.trim(),
            })));
        } catch (error) {
            console.error('💥 Unexpected error in getProviderLocations:', error);
            setLocations([]);
        }
    };
    const getProviderDetails = async (providerId) => {
        if (!providerId) return;

        // Use cached provider object from provider-full-details when available
        const cached = providerFullDetails.find((item) => (item.provider ?? item).id === Number(providerId));
        if (cached) {
            setFormData((prev) => ({ ...prev, providerDetail: cached.provider ?? cached }));
            return;
        }

        if (!token) return;
        try {
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider/${providerId}`,
                { method: 'GET', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` } }
            );
            const result = await response.json();
            setFormData((prev) => ({
                ...prev,
                providerDetail: (response.ok && !result.error && result.success && result.data) ? result.data : null,
            }));
        } catch (error) {
            console.error('💥 Unexpected error in getProviderDetails:', error);
            setFormData((prev) => ({ ...prev, providerDetail: null }));
        }
    };

    const getBusinessLocations = async () => {
        try {
            if (!business?.id) {
                setLocations([]);
                return;
            }

            if (!token) {
                console.error('No authentication token found');
                setLocations([]);
                return;
            }

            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/get-location`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                console.error('❌ Error fetching business locations:', result.error || 'Failed to fetch locations');
                setLocations([]);
                return;
            }

            // Backend returns { success: true, data: [] } or array directly
            const data = result.success && result.data !== undefined ? result.data : (Array.isArray(result) ? result : []);

            if (!data || data.length === 0) {
                setLocations([]);
                return;
            }

            const locations = data.map((l) => ({
                id: l.id,
                location: (l.name + ' ' + l.address + ' ' + l.city + ' ' + l.state + ' ' + l.suite + ' ' + l.zip).trim(),
            }));

            setLocations(locations);
        } catch (error) {
            console.error('💥 Unexpected error in getBusinessLocations:', error);
            setLocations([]);
        }
    };

    const getServices = async () => {
        try {
            // Validate business_id
            if (!business?.id) {
                console.error('Business ID is required');
                setServices([]);
                return;
            }

            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setServices([]);
                return;
            }

            // Fetch services from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/services`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch services';
                console.error('❌ Error fetching business services:', errorMessage);
                setServices([]);
                return;
            }

            // Backend returns { success: true, data: [] }
            const data = result.data || [];

            // Handle case where data is null or empty - still show "+ Add Service" option
            const list = !data || data.length === 0 ? [] : (Array.isArray(data) ? data : [data]);
            if (list.length === 0) {
                console.warn('⚠️ No bookable services found for business ID:', business?.id);
            }
            // Put "+ Add new service" at top of dropdown (same as ProviderPopup)
            setServices([{ id: 'add_new_service', name: '+ Add new service' }, ...list]);
        } catch (error) {
            console.error('💥 Unexpected error in getServices:', error);
            setServices([]);
        }
    };

    useEffect(() => {
        // Load all business services when component opens or business data is available
        if (open && business?.id) {
            getServices()
                .then(() => {
                    // If this is an edit with existing service data, make sure to preserve it
                    if (formData?.form_status === 'edit' && formData?.servicesData?.length > 0) {
                        setServicesCount(formData?.servicesData?.length);
                    } else {
                        setServicesCount(1);
                    }
                })
                .catch((error) => {
                    console.error('❌ Error in getServices promise:', error);
                    setServices([]);
                    setServicesCount(1);
                });
        }
    }, [open, business?.id]);

    useEffect(() => {
        if (!formData?.provider) return;

        const isBusinessSelected = typeof formData?.provider === 'string' && String(formData?.provider).startsWith('business_');
        if (isBusinessSelected) {
            // For business selection, fetch business locations (not provider-specific)
            setProviderAvailabilityData(null);
            setFormData(prev => ({
                ...prev,
                providerDetail: null,
            }));
            getBusinessLocations();
            return;
        }

        if (lastFetchedProviderRef.current === String(formData?.provider)) return;
        lastFetchedProviderRef.current = String(formData?.provider);

        getProviderLocations(formData?.provider)
            .then(() => {
            })
            .catch((error) => {
                setLocations([]);
            });

        getProviderDetails(formData?.provider)
            .then(() => {
            })
            .catch((error) => {
                console.error('❌ Error in getProviderDetails promise:', error);
            });
    }, [formData?.provider, business?.id]);

    useEffect(() => {
        // Only sync servicesCount with servicesData on initial load, not on every change
        // This prevents conflicts with manual updates from handleAddAnotherService and handleRemoveService
        if (formData?.form_status === 'edit' && formData?.servicesData && formData?.servicesData?.length > 0) {
            // Only set count for edit mode on initial load (when count is still 1)
            if (servicesCount === 1) {
                setServicesCount(formData?.servicesData?.length);
            }
        }
    }, [open, formData?.form_status]); // Only run on open or form_status change, not on servicesData changes

    // Add validation to handleSubmit
    const onSubmit = async (e) => {
        try {
            e.preventDefault();
            
            if (!isFormValid()) return;
            
            setIsSubmitting(true);
            
            // For new patients, check email one more time before submitting
            if (formData?.form_status === 'add' && !formData?.patient && formData?.email) {
                const emailExists = await checkEmailExists(formData.email);
                if (emailExists) {
                    toasterNotification('error', '', 'This email already exists. Please select the existing patient or use a different email.');
                    return;
                }
            }

            // Validate that every service is from the dropdown (or add new service first)
            if (formData?.form_status === 'add' && formData?.servicesData?.length) {
                const validServiceIds = new Set(
                    (services || []).filter((s) => s.id !== 'add_new_service').map((s) => Number(s.id))
                );
                const hasInvalidService = formData.servicesData.some((item) => {
                    const name = (item?.serviceName ?? '').toString().trim();
                    if (!name) return false;
                    const id = item?.service;
                    if (id === null || id === undefined || id === '') return true;
                    const numId = Number(id);
                    if (isNaN(numId) || !validServiceIds.has(numId)) return true;
                    return false;
                });
                if (hasInvalidService) {
                    toasterNotification('info', '', 'One or more services are not in the list. Please select a service from the dropdown or add that new service first.');
                    return;
                }
            }
            
            await handleSubmit(e);
        } catch (error) {
            console.error('Error in onSubmit:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Build contextual subtext for the header
    const isPendingStatus = String(formData?.appointmentStatus || '')
        .toLowerCase()
        .includes('pending');
    const isPastStatus = String(formData?.appointmentStatus || '')
        .toLowerCase()
        .includes('past');

    const patientDisplayName = formData?.patient_name || formData?.name || '';
    const patientEmail = (formData?.new_patient && formData?.new_patient?.email) || formData?.email || '';

    const getHeaderSubText = () => {
        if (formData.form_status === 'add') {
            return 'Fill out this form to create an appointment for new or existing patients. They will receive a confirmation email to review, accept, and pay for the appointment.';
        }

        if (formData.form_status === 'edit' && isPendingStatus) {
            // Contextual note for pending appointments (no summary)
            const parts = [];
            parts.push('This appointment is pending patient review and payment.');
            if (patientEmail) {
                parts.push(`An email has been sent to ${patientDisplayName || 'the patient'}${patientEmail ? ` (${patientEmail})` : ''} with a link to confirm.`);
            } else {
                parts.push('An email has been sent to the patient with a link to confirm.');
            }
            parts.push('If you make changes, use Save & Resend to send an updated email.');
            return parts.join(' ');
        }

        if (formData.form_status !== 'add' && isPastStatus) {
            // Contextual note for past appointments
            return 'This appointment is in the past and was never confirmed or paid by the patient. No payment was processed. You can reschedule by creating a new appointment or leave this record for reference.';
        }

        // Default (non-pending edit/view) – no extra summary text
        return '';
    };

    return (
        <>
            <FormModal
                open={open}
                onClose={handleClose}
                title={
                    formData.form_status === 'add'
                        ? 'Create an Appointment'
                        : (formData?.patient_name && formData?.patient_name.trim().length > 0
                            ? `Appointment for ${formData.patient_name}`
                            : 'Appointment')
                }
                subText={getHeaderSubText()}
                badge={
                    formData?.form_status !== 'add' && (
                        <AppointmentBadge appointmentStatus={formData?.appointmentStatus} />
                    )
                }
                stickyFooter={
                    formData.form_status === 'add' ? (
                        <StickyActionFooter
                            onCancel={() => {
                                setServicesCount(1);
                                onClose();
                            }}
                            onSave={(e) => {
                                e.preventDefault();
                                onSubmit(e);
                            }}
                            cancelText="Cancel"
                            saveText="Create Appointment"
                            isLoading={isSubmitting}
                            disabled={!isFormValid() || isSubmitting || hasEmailError}
                        />
                    ) : formData.form_status === 'edit' ? (
                        <StickyActionFooter
                            onCancel={
                                !hasFormChanges && isNewPatientAppointment() && !isAppointmentCancelled()
                                    ? handleResendEmail
                                    : undefined
                            }
                            onSave={(e) => {
                                if (isAppointmentCancelled()) return null;
                                if (hasFormChanges) {
                                    if (isNewPatientAppointment()) {
                                        return handleSaveAndResend(e);
                                    }
                                    e.preventDefault();
                                    return onSubmit(e);
                                }
                                return onClose();
                            }}
                            cancelText={!hasFormChanges && isNewPatientAppointment() && !isAppointmentCancelled()
                                ? (isResendingEmail ? 'Resending...' : 'Resend Email')
                                : undefined}
                            saveText={isAppointmentCancelled()
                                ? 'Save'
                                : (hasFormChanges
                                    ? (hasRescheduleRelevantChanges
                                        ? 'Reschedule'
                                        : (isNewPatientAppointment() ? 'Save & Resend' : 'Save'))
                                    : 'Done')}
                            isLoading={isSubmitting || isResendingEmail}
                            disabled={(hasFormChanges && !isFormValid()) || isSubmitting || isAppointmentCancelled() || isResendingEmail}
                            showDelete={shouldShowCancelButton()}
                            onDelete={() => {
                                cancelAppointment(formData.id).then((isCancelled) => {
                                    // Only refresh and reset form state if the appointment
                                    // was actually cancelled (user confirmed and update succeeded)
                                    if (isCancelled) {
                                        if (getAppointmentList) getAppointmentList();
                                        setFormData({ ...formData, form_status: 'add' });
                                    }
                                });
                            }}
                            deleteText="Cancel Appointment"
                        />
                    ) : formData.form_status === 'view' ? (
                        <StickyActionFooter
                            onCancel={undefined}
                            onSave={onClose}
                            saveText="Done"
                            isLoading={false}
                            disabled={false}
                        />
                    ) : null
                }
            >
                <FormStyle onSubmit={onSubmit}>
                    <Form
                        formData={formData}
                        setFormData={setFormData}
                        handleChange={handleFormChange}
                        servicesCount={servicesCount}
                        handleServiceChange={handleServiceChange}
                        services={services}
                        business={business}
                        handleRemoveService={handleRemoveService}
                        providers={providers}
                        locations={locations}
                        providerAvailabilityData={providerAvailabilityData}
                        setProviders={setProviders}
                        handleAddAnotherService={handleAddAnotherService}
                        oooData={(() => {
                            const pid = formData?.provider;
                            if (!pid || String(pid).startsWith('business_')) return [];
                            const cached = providerFullDetails.find(
                                (item) => (item.provider ?? item).id === Number(pid)
                            );
                            return cached ? (Array.isArray(cached.ooo) ? cached.ooo : []) : undefined;
                        })()}
                        onEmailValidChange={(valid) => setHasEmailError(!valid)}
                        cachedProviderAppointments={(() => {
                            const pid = formData?.provider;
                            if (!pid || String(pid).startsWith('business_')) return undefined;
                            const cached = providerFullDetails.find(
                                (item) => (item.provider ?? item).id === Number(pid)
                            );
                            return cached ? (Array.isArray(cached.appointments) ? cached.appointments : []) : undefined;
                        })()}
                    />
                </FormStyle>
            </FormModal>

            <ServicePopup
                open={openServicePopup}
                onClose={() => {
                    setOpenServicePopup(false);
                    setCurrentServiceIndex(null);
                }}
                businessId={business?.id}
                getServiceList={getServices}
                onServiceAdded={(newService) => {
                    if (currentServiceIndex === null || !newService) return;
                    const price = newService.price != null ? String(Number(newService.price).toFixed(2)) : '';
                    setServices((prevServices) => {
                        const list = Array.isArray(prevServices) ? prevServices : [];
                        const nextServiceId = Number(newService.id);
                        const alreadyExists = list.some((service) => Number(service?.id) === nextServiceId);
                        if (alreadyExists) return list;

                        const addNewOption = list.find((service) => service?.id === 'add_new_service');
                        const existingServices = list.filter((service) => service?.id !== 'add_new_service');
                        const appendedService = {
                            ...newService,
                            id: newService.id,
                            name: newService.name || newService.service,
                        };
                        return addNewOption
                            ? [addNewOption, ...existingServices, appendedService]
                            : [...existingServices, appendedService];
                    });
                    setFormData((prev) => {
                        const servicesData = [...(prev?.servicesData || [])];
                        if (!servicesData[currentServiceIndex]) servicesData[currentServiceIndex] = {};
                        servicesData[currentServiceIndex] = {
                            ...servicesData[currentServiceIndex],
                            service: newService.id,
                            serviceName: newService.name || newService.service,
                            description: newService.description ?? servicesData[currentServiceIndex]?.description ?? '',
                            price,
                        };
                        return { ...prev, servicesData };
                    });
                }}
            />
        </>
    );
};

export default AppointmentPopup;
